import type { User } from '../../types';

import { estClient } from './estClient';
import { JsonParseError } from './httpClient';

export interface Task {
  id: string;
  unit_id: string;
  lead_id: string | null;
  lead_status_id: string | null;
  lead_status_name: string | null;
  deal_id: string | null;
  object_id: string | null;

  /**
   * User id
   */
  for_user: string;

  /**
   * Task description
   */
  task: string;
  comment: string;
  sort: '0';

  /**
   * @example '2018-03-20 22:00:00'
   */
  created_at: string;
  created_by: string | null;

  /**
   * @example '2018-03-20 22:00:00'
   */
  due_date: string;

  /**
   * - 0 — not solved
   * - 1 — solved (successful meeting for meeting_type > 0)
   * - 2 — unsuccessful meeting for meeting_type > 0
   * - 3 — canceled meeting for meeting_type > 0
   */
  solved: '0' | '1' | '2' | '3';

  /**
   * @example '2018-03-20 22:00:00'
   */
  solved_at: string | null;
  solved_by: string | null;

  /**
   * TASK_TYPE_NORMAL = 0
   * TASK_TYPE_MEETING_OBJECT = 1
   * TASK_TYPE_MEETING_OFFICE = 2
   * TASK_TYPE_MEETING_ONLINE = 3
   */
  meeting_type: '0' | '1' | '2' | '3';

  user: Omit<User, 'current'>;
}

export type TaskEditableProps = Pick<
  Task,
  'task' | 'for_user' | 'due_date' | 'solved' | 'object_id' | 'meeting_type'
>;

class TasksApi {
  async getUnitTasks(unitId: string): Promise<Task[]> {
    try {
      return await estClient.get<Task[]>(`/api/unit/${unitId}/tasks`);
    } catch (error) {
      if (error instanceof JsonParseError) {
        // TODO: we have an issue in Controller::successResponse() where it
        // responds with empty body instead of json when getting the response is empty array
        // so handling this case here till it will get fixed
        console.warn('Json parse error', { error });

        return [];
      }

      throw error;
    }
  }

  async getDealTasks(dealId: string): Promise<Task[]> {
    try {
      return await estClient.get<Task[]>(`/api/deal/${dealId}/tasks`);
    } catch (error) {
      if (error instanceof JsonParseError) {
        // TODO: we have an issue in Controller::successResponse() where it
        // responds with empty body instead of json when getting the response is empty array
        // so handling this case here till it will get fixed
        console.warn('Json parse error', { error });

        return [];
      }

      throw error;
    }
  }

  async getLeadTasks(leadId: string): Promise<Task[]> {
    try {
      return await estClient.get<Task[]>(`/api/lead/${leadId}/tasks`);
    } catch (error) {
      if (error instanceof JsonParseError) {
        // TODO: we have an issue in Controller::successResponse() where it
        // responds with empty body instead of json when getting the response is empty array
        // so handling this case here till it will get fixed
        console.warn('Json parse error', { error });

        return [];
      }

      throw error;
    }
  }

  create(
    task: TaskEditableProps & {
      type: 'deal_id' | 'unit_id' | 'lead_id';

      /**
       * Entity id to relate this task with
       */
      for_id: string;
    },
  ): Promise<Task> {
    return estClient.post<Task>('/api/task/create', {
      body: task,
    });
  }

  update(task: TaskEditableProps & Pick<Task, 'id'>): Promise<Task> {
    return estClient.post<Task>(`/api/task/${task.id}/update`, {
      body: task,
    });
  }

  get MEETING_TYPE() {
    return {
      NONE: '0',
      OBJECT: '1',
      OFFICE: '2',
      ONLINE: '3',
    } as const;
  }

  get RESULT() {
    return {
      NOT_SOLVED: '0',
      SOLVED: '1', // successful
      UNSUCCESSFUL: '2',
      CANCELED: '3',
    } as const;
  }
}

export const tasksApi = new TasksApi();
